import type { AgentActionKey } from './actions';
import type { OrganizationType } from '../domain/organization-type';

export const AGENT_SCREEN_PATHS = [
  '/',
  '/parts/import',
  '/parts/:id',
  '/parts',
  '/rfqs/:id',
  '/rfqs',
  '/quotes/:id',
  '/orders/management/:id',
  '/orders/:id',
  '/orders',
  '/storefront',
  '/settings',
  '/membership',
] as const;

export type AgentScreenPath = (typeof AGENT_SCREEN_PATHS)[number];

export interface AgentScreenDefinition {
  readonly label: string;
  readonly orgTypes: readonly OrganizationType[];
  readonly suggestedPrompts: readonly string[];
  readonly suggestionKeys: readonly AgentActionKey[];
}

const BOTH: readonly OrganizationType[] = ['purchaser', 'supplier'];

/* Everything about a screen that is not a database read */
export const AGENT_SCREENS: Record<AgentScreenPath, AgentScreenDefinition> = {
  '/': {
    label: 'the dashboard',
    orgTypes: BOTH,
    suggestedPrompts: ['What needs my attention today?', 'Which RFQs are waiting on me?'],
    suggestionKeys: ['open_rfqs_list', 'open_orders_list', 'open_parts_list'],
  },
  '/parts/import': {
    label: 'the part import page',
    orgTypes: BOTH,
    suggestedPrompts: ['What file formats can I upload?', 'What columns does the CSV need?'],
    suggestionKeys: ['open_parts_list'],
  },
  '/parts/:id': {
    label: 'the part detail page',
    orgTypes: BOTH,
    suggestedPrompts: [
      'Why does this part score the way it does?',
      'What is missing before I can quote this?',
      'Which suppliers could make this part?',
    ],
    suggestionKeys: ['open_rfq', 'open_parts_list', 'compare_quotes_for_rfq'],
  },
  '/parts': {
    label: 'the parts list',
    orgTypes: BOTH,
    suggestedPrompts: [
      'Which of these parts are missing data?',
      'Which parts are best suited to additive?',
    ],
    suggestionKeys: ['open_part', 'import_parts', 'open_parts_list'],
  },
  '/rfqs/:id': {
    label: 'the RFQ detail page',
    orgTypes: BOTH,
    suggestedPrompts: [
      'Which of these quotes is actually the best value?',
      'What is the real difference between these offers?',
    ],
    suggestionKeys: ['compare_quotes_for_rfq', 'open_quote', 'open_part'],
  },
  '/rfqs': {
    label: 'the RFQ list',
    orgTypes: BOTH,
    suggestedPrompts: ['Which RFQs are waiting on a quote?', 'Which of these should I prioritise?'],
    suggestionKeys: ['open_rfq', 'open_rfqs_list'],
  },
  '/quotes/:id': {
    label: 'the quote detail page',
    orgTypes: BOTH,
    suggestedPrompts: ['Is this quote good value?', 'How does this compare to the others?'],
    suggestionKeys: ['compare_quotes_for_rfq', 'open_rfq'],
  },
  '/orders/management/:id': {
    label: 'the order management page',
    orgTypes: ['supplier'],
    suggestedPrompts: ['What is the next step on this order?', 'What do I need to ship?'],
    suggestionKeys: ['open_orders_list'],
  },
  '/orders/:id': {
    label: 'the order detail page',
    orgTypes: ['purchaser'],
    suggestedPrompts: ['What is the status of this order?', 'When should this arrive?'],
    suggestionKeys: ['open_orders_list'],
  },
  '/orders': {
    label: 'the orders list',
    orgTypes: BOTH,
    suggestedPrompts: ['Which orders are late?', 'What is shipping this week?'],
    suggestionKeys: ['open_order', 'open_order_management', 'open_orders_list'],
  },
  '/storefront': {
    label: 'the storefront editor',
    orgTypes: ['supplier'],
    suggestedPrompts: [
      'What capabilities do I have listed?',
      'Which capabilities am I missing for the RFQs I see?',
      'How could my storefront win more work?',
    ],
    suggestionKeys: ['open_storefront_capabilities', 'open_rfqs_list'],
  },
  '/settings': {
    label: 'the settings page',
    orgTypes: BOTH,
    suggestedPrompts: ['How do I invite a colleague?'],
    suggestionKeys: ['open_team_settings'],
  },
  '/membership': {
    label: 'the membership page',
    orgTypes: ['supplier'],
    suggestedPrompts: ['What do the membership tiers change?'],
    suggestionKeys: [],
  },
};

/** Kept as its own export: the audience is what the router guard is checked against. */
export const AGENT_SCREEN_ORG_TYPES: Record<AgentScreenPath, readonly OrganizationType[]> =
  Object.fromEntries(
    AGENT_SCREEN_PATHS.map((path) => [path, AGENT_SCREENS[path].orgTypes]),
  ) as Record<AgentScreenPath, readonly OrganizationType[]>;

export function isAgentScreenPath(value: string): value is AgentScreenPath {
  return (AGENT_SCREEN_PATHS as readonly string[]).includes(value);
}
